import React from "react";
import ReactPaginate from "react-paginate";
import eventRefs from "../../assets/eventRef";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import axios from "axios";
import LoadingSpinner from "../../components/common/loading";

const EVENT_REF_MAP = new Map(eventRefs.map((er) => [er.code, er]));
const DEFAULT_SORT_DIRECTIONS = {
  eventName: "asc",
  lastTriggered: "desc",
};
const DEFAULT_PAGE_SIZE = 25;

const compareStrings = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    sensitivity: "base",
    numeric: true,
  });

const parseSortFilter = (sortFilter = "") => {
  const [sortKeyRaw = "", sortDirectionRaw = ""] = (sortFilter || "").split("_");
  const validKeys = ["eventName", "lastTriggered"];
  const sortKey = validKeys.includes(sortKeyRaw) ? sortKeyRaw : "";

  if (!sortKey) {
    return { sortKey: "", sortDirection: "" };
  }

  const defaultDirection = DEFAULT_SORT_DIRECTIONS[sortKey] || "asc";
  const sortDirection = ["asc", "desc"].includes(sortDirectionRaw)
    ? sortDirectionRaw
    : defaultDirection;

  return { sortKey, sortDirection };
};

const buildEventNameOptions = (codes = []) => {
  if (!Array.isArray(codes)) {
    return [];
  }

  const uniqueCodes = Array.from(
    new Set(
      codes
        .map((code) => Number(code))
        .filter((code) => Number.isFinite(code))
    )
  );

  return uniqueCodes
    .map((code) => {
      const label = EVENT_REF_MAP.get(code)?.name || `Event Code ${code}`;

      return {
        value: String(code),
        label,
      };
    })
    .sort((a, b) => compareStrings(a.label, b.label));
};


export default class DeviceAlerts extends React.Component {
  state = {
    searchBar: "",
    currentFilter: "",
    eventNameFilter: "",
    eventTriggeredFilter: "Alert",
    sortFilter: "",

    events: [],
    eventsLoading: false,
    refreshing: new Set(),
    page: 1,
    totalPages: 0,
    totalCount: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    eventNameOptions: [],
  };
  applyFilters = () => {
    this.setState(
      {
        currentFilter: this.state.searchBar,
        page: 1,
      },
      () => {
        this.fetchEvents({ page: 1 });
      }
    );
  };
  componentDidMount() {
    // if (
    //   !this.props.resources.eventsLoaded &&
    //   !this.props.resources.eventsLoading
    // ) {
    //   this.props.fetchResource("events", () => {}, "", "v2");
    // }
    this.fetchEvents({ page: 1 });
  }

  componentDidUpdate(prevProps) {
    const prevQuery = prevProps.queryParams || {};
    const nextQuery = this.props.queryParams || {};

    const prevKeys = Object.keys(prevQuery);
    const nextKeys = Object.keys(nextQuery);

    const queryChanged =
      prevKeys.length !== nextKeys.length ||
      prevKeys.some((key) => prevQuery[key] !== nextQuery[key]);

    if (queryChanged) {
      this.setState(
        {
          page: 1,
        },
        () => {
          this.fetchEvents({ page: 1 });
        }
      );
    }
  }

  fetchEvents = ({ page } = {}) => {
    const nextPage = page || this.state.page || 1;

    this.setState({
      eventsLoading: true,
    });

    const params = new URLSearchParams();
    const { queryParams } = this.props;

    Object.entries(queryParams || {}).forEach(([key, value]) => {
      if (value == null || value === "") {
        return;
      }

      params.append(key, value);
    });

    params.set("page", nextPage);
    params.set("limit", this.state.pageSize);

    if (this.state.sortFilter) {
      params.set("sort", this.state.sortFilter);
    }

    if (this.state.currentFilter) {
      params.set("search", this.state.currentFilter);
    }

    if (this.state.eventNameFilter) {
      params.set("code", this.state.eventNameFilter);
    }

    if (this.state.eventTriggeredFilter) {
      if (this.state.eventTriggeredFilter === "Alert") {
        params.set("triggered", "true");
      } else if (this.state.eventTriggeredFilter === "OK") {
        params.set("triggered", "false");
      }
    }

    const url = `/v3/events?${params.toString()}`;

    axios
      .get(url, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      })
      .then((response) => {
        const payload = response?.data || {};
        const events = Array.isArray(payload.data) ? payload.data : [];
        const meta = payload.meta || {};
        let eventNameOptions = buildEventNameOptions(meta.eventCodes || []);

        if (this.state.eventNameFilter) {
          const hasSelected = eventNameOptions.some(
            (option) => option.value === this.state.eventNameFilter
          );

          if (!hasSelected) {
            const numericCode = Number(this.state.eventNameFilter);
            eventNameOptions = [
              ...eventNameOptions,
              {
                value: this.state.eventNameFilter,
                label:
                  EVENT_REF_MAP.get(numericCode)?.name ||
                  `Event Code ${this.state.eventNameFilter}`,
              },
            ].sort((a, b) => compareStrings(a.label, b.label));
          }
        }

        this.setState({
          events,
          eventsLoading: false,
          page: meta.currentPage || nextPage,
          totalPages: meta.totalPages || 0,
          totalCount: meta.totalCount || 0,
          eventNameOptions,
        });
      })
      .catch((error) => {
        console.log("Error fetching events", error);
        this.setState({
          eventsLoading: false,
        });
      });
  };

  handleSort = (sortKey) => {
    if (!sortKey) {
      this.setState(
        {
          sortFilter: "",
          page: 1,
        },
        () => {
          this.fetchEvents({ page: 1 });
        }
      );
      return;
    }

    this.setState(
      (prevState) => {
        const [currentKey, currentDirection] = (prevState.sortFilter || "").split("_");
        const defaultDirection = DEFAULT_SORT_DIRECTIONS[sortKey] || "asc";
        const oppositeDirection = defaultDirection === "asc" ? "desc" : "asc";

        let nextSortFilter = `${sortKey}_${defaultDirection}`;

        if (currentKey === sortKey) {
          if (currentDirection === defaultDirection) {
            nextSortFilter = `${sortKey}_${oppositeDirection}`;
          } else if (currentDirection === oppositeDirection) {
            nextSortFilter = "";
          }
        }

        return {
          sortFilter: nextSortFilter,
          page: 1,
        };
      },
      () => {
        this.fetchEvents({ page: 1 });
      }
    );
  };

  handleSortKeyDown = (event, sortKey) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.handleSort(sortKey);
    }
  };

  refreshEvent = (enclosureSerialNumber, _eventId) => {
    if (this.state.refreshing.has(enclosureSerialNumber)) return;

    // 1️⃣ mark “in progress”
    this.setState(prev => ({
      refreshing: new Set(prev.refreshing).add(enclosureSerialNumber)
    }));
    this.props.getDeviceItemAndData(
      enclosureSerialNumber,
      (err, _data) => {
        if (err) {
          console.log("Error getting device item and data", err);
        }

        this.setState(
          prev => {
            const next = new Set(prev.refreshing);
            next.delete(enclosureSerialNumber);
            return { refreshing: next };
          },
          () => {
            if (!err) {
              this.fetchEvents({ page: this.state.page });
            }
          }
        );
      },
      enclosureSerialNumber
    );


  }
  render() {
    const {
      events,
      eventsLoading,
      sortFilter,
      eventNameOptions,
      page,
      totalPages,
      totalCount,
      pageSize,

      // devices

    } = this.state;

    const currentPage = page || 1;
    let summaryText = null;
    if (!(eventsLoading && events.length === 0)) {
      if (typeof totalCount === "number") {
        if (totalCount === 0) {
          summaryText = "Showing 0 of 0 alerts";
        } else if (events.length === 0) {
          summaryText = `Showing 0 of ${totalCount} alerts`;
        } else {
          const showingStart = (currentPage - 1) * pageSize + 1;
          const showingEnd = Math.min(showingStart + events.length - 1, totalCount);
          summaryText = `Showing ${showingStart}-${showingEnd} of ${totalCount} alerts`;
        }
      }
    }

    const { sortKey, sortDirection } = parseSortFilter(sortFilter);

    const columnWidths = [
      9, //0
      16, //1
      4, //2
      6, //3
      4, //4
      5, //5
      9, //6
      13, //7
      7, //8
    ];
    const sortOptions = [
      { label: "Default", value: "" },
      { label: "Event Name A-Z", value: "eventName_asc" },
      { label: "Event Name Z-A", value: "eventName_desc" },
      { label: "Last Triggered (Newest)", value: "lastTriggered_desc" },
      { label: "Last Triggered (Oldest)", value: "lastTriggered_asc" },
    ];

    return (
      <div
        style={{
          overflowX: "auto",
        }}
      >
        <div
          className="grid-x"
          style={{ marginBottom: "10px", marginTop: "20px" }}
        >
          <div className="cell show-for-small-only">
            {this.props.match.params.enclosureSerialNumber ? (
              <div className="">
                <div
                  style={{
                    marginTop: "20px",
                  }}
                >
                  <Link
                    to={`/devices/connected/${this.props.match.params.enclosureSerialNumber}/alerts/new`}
                  >
                    <div
                      className="button"
                      style={{
                        marginBottom: "10px",
                        width: "100%",
                      }}
                    >
                      <span>+ Create New Alert Event</span>
                    </div>
                  </Link>
                </div>
              </div>
            ) : (
              null
            )}
          </div>
          <div className="cell large-8 medium-8">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                // marginBottom: "10px",
              }}
            >
              <input
                type="text"
                style={{
                  margin: "0px",
                }}
                placeholder="Search for device name, serial number, event name, event parameter"
                value={this.state.searchBar}
                onChange={(e) => {
                  this.setState({ searchBar: e.target.value });
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    this.applyFilters();
                  }
                }}
              />
              <span>
                <span
                  className="search-icon-button"
                  style={{ margin: "0 0 1rem", marginBottom: "0px" }}
                  onClick={() => {
                    this.applyFilters();
                  }}
                >
                  <i className="material-icons">search</i>
                </span>
              </span>
            </div>
          </div>

          <div className="cell large-4 medium-4 hide-for-small-only">
            {this.props.match.params.enclosureSerialNumber ? (
              <div
                className=""
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    width: "300px",
                    // padding: "10px"
                  }}
                >
                  <div>
                    <Link
                      to={`/devices/connected/${this.props.match.params.enclosureSerialNumber}/alerts/new`}
                    >
                      <div
                        className="button"
                        style={{
                          marginBottom: "0px",
                        }}
                      >
                        <span>+ Create New Alert Event</span>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              null
            )}
          </div>
        </div>
        <div className="grid-x">
          <div className="cell large-6">
            <div
              style={{
                fontSize: "14px",
                color: "#6b6b6b",
                marginBottom: "5px",
                marginLeft: "3px",
              }}
            >
              Filter by
            </div>
            <div
              style={{
                display: "flex",
                marginBottom: "10px",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <span>
                <select
                  style={{
                    margin: "0px",
                    width: "275px"
                  }}
                onChange={(e) => {
                  const value = e.target.value;
                  this.setState(
                    {
                      eventNameFilter: value,
                      page: 1,
                    },
                    () => {
                      this.fetchEvents({ page: 1 });
                    }
                  );
                }}
                value={this.state.eventNameFilter}
                >
                  <option value="">All Event Types</option>
                  <optgroup label="Filter by">
                    {eventNameOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </span>
              <span>
                <select
                  style={{
                    // backgroundColor: "transparent",
                    // border: "none",
                    // cursor: 'pointer',
                    margin: "0px",
                    // color: "#6b6b6b",
                    // fontSize: "14px",
                  }}
                onChange={(e) => {
                  const value = e.target.value;
                  this.setState(
                    {
                      eventTriggeredFilter: value,
                      page: 1,
                    },
                    () => {
                      this.fetchEvents({ page: 1 });
                    }
                  );
                }}
                  value={this.state.eventTriggeredFilter}
                >
                  <option value="">All Status</option>
                  <optgroup label="Filter by">
                    <option value="OK">OK (Normal)</option>
                    <option value="Alert">Alert Activated</option>
                  </optgroup>
                </select>
              </span>
              
            </div>
          </div>
          <div className="cell large-6" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
            <Link to="/alerts-documentation">

              <div className="button" style={{ display: 'flex', alignItems: 'center', margin: "0px"}}>
              <i className="material-icons">help</i>
                <div style={{ color: "white" }}>
                  Learn More about Alerts
                </div>
              </div>
              </Link>
            </div>
          </div>
        </div>
        <div className="grid-x">
          <div className="cell large-6" style={{ marginBottom: "20px" }}>
                <span
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    color: "#6b6b6b",
                    marginBottom: "5px",
                    marginLeft: "3px",
                  }}
                >
                  Sort by
                </div>
                <select
                  style={{
                    margin: "0px",
                    // maxWidth: "275px"
                    width: "275px"
                  }}
                value={sortFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  this.setState(
                    {
                      sortFilter: value,
                      page: 1,
                    },
                    () => {
                      this.fetchEvents({ page: 1 });
                    }
                  );
                }}
                >
                  {sortOptions.map((option) => (
                    <option
                      key={option.value || "default"}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </span>
          </div>
        </div>

        {summaryText && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              color: "#6b7280",
              fontSize: "14px",
              margin: "0 0 8px",
            }}
          >
            {summaryText}
          </div>
        )}
        <div className="grid-row header">
          {/* <div style={{ flex: columnWidths[0] }}>Category</div> */}
          <div
            style={{
              flex: columnWidths[1],
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
            }}
            role="button"
            tabIndex={0}
            onClick={() => this.handleSort("eventName")}
            onKeyDown={(event) => this.handleSortKeyDown(event, "eventName")}
          >
            Event Name
            {sortKey === "eventName" && (
              <span style={{ marginLeft: "4px", fontSize: "12px" }}>
                {sortDirection === "asc" ? "▲" : "▼"}
              </span>
            )}
          </div>
          <div style={{ flex: columnWidths[2] }}>
            Status
          </div>
          <div
            style={{
              flex: columnWidths[6],
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
            }}
            role="button"
            tabIndex={0}
            onClick={() => this.handleSort("lastTriggered")}
            onKeyDown={(event) => this.handleSortKeyDown(event, "lastTriggered")}
          >
            Last Triggered
            {sortKey === "lastTriggered" && (
              <span style={{ marginLeft: "4px", fontSize: "12px" }}>
                {sortDirection === "asc" ? "▲" : "▼"}
              </span>
            )}
          </div>
          <div style={{ flex: columnWidths[7] }}>Details</div>
          <div style={{ flex: columnWidths[7] }}>PDU</div>
          <div style={{ flex: columnWidths[8] }}></div>
        </div>
        {eventsLoading ? (
          <Loading columnWidths={columnWidths} />
        ) : events.length === 0 ? (
          <div className="grid-row-wrapper">
            <div className="grid-row">
              <div style={{ paddingLeft: "30px" }}>No Events Found</div>
            </div>
          </div>
        ) : null}
        {events.map((event, index) => {
          const foundEventRef =
            eventRefs.find((er) => er.code === event.code) || {};

          const eventRefName = foundEventRef.name;
          const foundEventRefDescription = event
            ? event.triggered
              ? foundEventRef?.triggeredDescription
              : foundEventRef?.notTriggeredDescription
            : "";
          const hydratedFoundEventRefDesc = (foundEventRefDescription || "").replace(
            /{{param(\d+)}}/g,
            (_, i) => {
              const idx = parseInt(i, 10) - 1;
              const rawVal = (event?.params || [])[idx] ?? "";
              if (event?.code === 12 && idx === 2) {
                if (rawVal === "1" || rawVal === 1) return "primary ethernet";
                if (rawVal === "2" || rawVal === 2) return "USB secondary ethernet";
                if (rawVal === "" ) return "primary ethernet"

              }
              return rawVal;
            }
          );

          const timeSinceTriggered =
            event.triggered &&
            event.timeExecutedInEpoch &&
            formatDistanceToNow(new Date(event.timeExecutedInEpoch * 1000), {
              addSuffix: true,
            });

          const timeSinceSync = event.lastChange ? formatDistanceToNow(new Date(event.lastChange)) : ""

          const eventKey = [
            event?.enclosureSerialNumber ?? "unknown",
            event?.id ?? index,
          ].join("::");

          return (
            <div
              key={eventKey}
              className="grid-row-wrapper"
              style={{
                opacity: event.connectionState === "Connected" ? 1 : 0.6
              }}
            >
              <div className="grid-row" style={{}}>
                <div
                  style={{
                    flex: columnWidths[1],
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  <Link
                    to={`/devices/connected/${event.enclosureSerialNumber}/alerts/${event.id}`}
                  >
                    <React.Fragment>
                      <span
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: "600",
                            textDecoration: "underline",
                          }}
                        >
                          {event.name}
                        </span>
                      </span>
                      <span style={{}}>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <span>{eventRefName}</span>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          ></span>
                        </span>
                      </span>
                    </React.Fragment>
                  </Link>
                </div>

                <div
                  style={{ flex: columnWidths[2] }}
                  title={event.triggered ? "ALERT" : "OK"}
                >
                  <div
                    className="spreadsheet-badge"
                    style={{
                      backgroundColor: event.triggered
                        ? "rgb(235 210 210)"
                        : "rgb(194, 223, 194)",
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{
                        color: event.triggered ? "maroon" : "forestgreen",
                        marginRight: "0px",
                      }}
                    >
                      {event.triggered ? "circle_notifications" : "task_alt"}
                    </i>
                    <b className="show-for-large-only">
                      {event.triggered ? "Alert" : " OK  "}
                    </b>
                  </div>
                </div>
                <div
                  style={{
                    flex: columnWidths[6],
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {timeSinceTriggered}
                </div>
                <div
                  style={{
                    flex: columnWidths[7],
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {hydratedFoundEventRefDesc}
                </div>
                <div style={{ flex: columnWidths[7] }}>
                  <Link
                    to={`/devices/connected/${event.enclosureSerialNumber}`}
                  >
                    <span>
                      <span>
                        {event.deviceName === "SynLink PDU" || !event.deviceName
                          ? `PDU SN:${event.enclosureSerialNumber}`
                          : <span style={{ display: 'flex', flexDirection: 'column' }}>
                              <span>
                                {event.deviceName}
                              </span>
                              <span>
                                {`SN: ${event.enclosureSerialNumber}`}
                              </span>
                            </span>}
                      </span>
                      <span>
                        {event.connectionState === "Disconnected" &&
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#6b6b6b",
                              marginLeft: "3px",
                            }}
                          >
                            (Disconnected)
                          </span>
                        }
                      </span>
                    </span>
                  </Link>
                </div>
              {/* <div style={{ flex: columnWidths[8] }}>
                <span
                  title="Refresh PDU Information"
                  className="black"
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: 'center'
                  }}
                  onClick={() => {
                    this.refreshEvent(
                      event.enclosureSerialNumber,
                      event.id,
                    );
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#6b6b6b",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Last sync:  {timeSinceSync}
                  </span>
                  <i
                    style={{ fontWeight: "600", color: "#6c6f72" }}
                    className="material-icons"
                  >
                    sync
                  </i>
                </span>
              </div> */}
                <div style={{ flex: columnWidths[8] }}>
                  {this.state.refreshing.has(event.enclosureSerialNumber) ? (
                    /* 🔄 spinner while that device is refreshing */
                    <LoadingSpinner black />

                  ) : (
                    <span
                      title="Refresh PDU Information"
                      className="black"
                      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                      onClick={() =>
                        this.refreshEvent(event.enclosureSerialNumber, event.id)
                      }
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6b6b6b",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Last sync: {timeSinceSync}
                      </span>
                      <i
                        style={{ fontWeight: "600", color: "#6c6f72" }}
                        className="material-icons"
                      >
                        sync
                      </i>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              forcePage={Math.max(0, (page || 1) - 1)}
              pageCount={totalPages}
              onPageChange={({ selected }) => {
                this.fetchEvents({ page: selected + 1 });
              }}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              containerClassName="react-paginate"
              activeClassName="selected"
            />
          </div>
        )}
      </div>
    );
  }
}

const Loading = ({ columnWidths }) => {
  return (
    <div style={{}}>
      {[null, null, null, null].map((item, index) => {
        return (
          <div className="grid-row-wrapper" key={index}>
            <div className="grid-row">
              {/* <div style={{ flex: columnWidths[0] }}></div> */}
              <div style={{ flex: columnWidths[1] }}>
                <div className="skeleton-blank" />
              </div>
              <div style={{ flex: columnWidths[2] }}>
                <div className="skeleton-blank" />{" "}
              </div>
              {/* <div style={{ flex: columnWidths[3] }}>
                <div className="skeleton-blank" />{" "}
              </div> */}
              <div style={{ flex: columnWidths[4] }}>
                <div className="skeleton-blank" />
              </div>
              <div style={{ flex: columnWidths[5] }}>
                <div className="skeleton-blank" />
              </div>
              {/* <div style={{ flex: columnWidths[6] }}>
                <div className="skeleton-blank" />{" "}
              </div> */}
              <div style={{ flex: columnWidths[7] }}>
                <div className="skeleton-blank" />
              </div>
              {/* <div style={{ flex: columnWidths[8] }}>
              <div className="skeleton-blank" />
            </div> */}
              {/* <div style={{ flex: columnWidths[9] }}>
                <div className="skeleton-blank" />{" "}
              </div> */}
            </div>
          </div>
        );
      })}
    </div>
  );
};
